import GraphQlApi from '@/api/graph/GraphQlApi';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import Resource from '@/api/Resource';
import Page from '@/api/graph/Page';
import networkBoundResource from '@/api/networkBoundResource';
import mapConnectionToPage from '@/api/mapConnectionToPage';
import unwrapGraphQlResponse from '@/api/unwrapGraphQlResponse';
import GlossFilter from '@/api/graph/GlossFilter';
import GlossOrdering, { OrderMode } from '@/api/graph/GlossOrdering';
import GlossSearchResult from '@/api/graph/GlossSearchResult';
import GlossMediaResult from '@/api/graph/GlossMediaResult';
import GlossMediaOverview from '@/api/graph/GlossMediaOverview';

export default class GlossRepository {
    private readonly api: GraphQlApi;

    public constructor(api: GraphQlApi) {
        this.api = api;
    }

    public findGlosses(
        first: number,
        after: string | null,
        where: GlossFilter,
        orderBy: GlossOrdering | null,
    ): Observable<Resource<Page<GlossSearchResult>>> {
        const observable = this.api.getGlossesPage(first, after, where, orderBy);
        return networkBoundResource(mapConnectionToPage(unwrapGraphQlResponse(observable)));
    }

    public findGlossesByKeyword(
        first: number,
        after: string | null,
        keyword: string,
        fromStartOfWord: boolean,
    ): Observable<Resource<Page<GlossSearchResult>>> {
        return this.findGlosses(
            first,
            after,
            { keywords_some: fromStartOfWord ? { value_starts_with: keyword } : { value_contains: keyword } },
            { name: OrderMode.ASC },
        );
    }

    public findGlossMediaById(id: string): Observable<Resource<GlossMediaOverview>> {
        const observable = this.api.getGlossMedia({ id });
        return networkBoundResource(
            unwrapGraphQlResponse(observable).pipe(
                map<GlossMediaResult | null, GlossMediaOverview>((t) => ({
                    name: t?.name || '',
                    drawings:
                        t?.variants.flatMap((v) =>
                            v.signDefinitions.flatMap((s) => s.drawings).map((d) => `${d.id!}?variant=${v.id!}`),
                        ) || [],
                    photos: t?.photos.map((p) => `${p.id!}?gloss=${t.id!}`) || [],
                    pictograms: t?.pictograms.map((p) => `${p.id!}?gloss=${t.id!}`) || [],
                })),
            ),
        );
    }
}
